import type { TScreenOrientation, TScreenRotation } from '@lucimark/shared/models/screen-rotation'

/** How the player realizes logical `screenRotation` on the current viewport. */
export type TViewportRotationMode = 'css' | 'native'

export interface ICssEffectiveRotationState {
  viewportRotationMode: TViewportRotationMode
  screenRotation: TScreenRotation
  /** Last Lucimark angle the Activity actually stuck at; null when CSS is the source of truth. */
  nativeAppliedRotation: TScreenRotation | null
}

const ANGLES: TScreenRotation[] = ['0', '90', '180', '270']

/** Clockwise CSS remainder after native Activity already applied `nativeApplied`. */
export function cssRotationDelta(
  logical: TScreenRotation,
  nativeApplied: TScreenRotation
): TScreenRotation {
  const d = (Number(logical) - Number(nativeApplied) + 360) % 360
  return (ANGLES.find(a => Number(a) === d) ?? '0')
}

/**
 * True when inner size already matches the portrait/landscape the logical angle wants.
 * Used to skip CSS on Android cold start after MainActivity `requestedOrientation` (phones),
 * while TVs that ignore orientation keep the CSS path.
 */
export function viewportAspectMatchesLogicalRotation(
  logical: TScreenRotation,
  width: number,
  height: number
): boolean {
  if (width <= 0 || height <= 0) {
    return false
  }
  const expectLandscape = logical === '0' || logical === '180'
  return (width >= height) === expectLandscape
}

/**
 * Rotation fed to CSS transforms, VBuffer, corner overlays, and live inner-size aspect math.
 * Native Activity orientation already remapped the window, so CSS uses the remainder vs the
 * last stuck native angle (identity when `logical === nativeApplied`).
 */
export function cssEffectiveScreenRotation(
  mode: TViewportRotationMode,
  logical: TScreenRotation,
  nativeApplied: TScreenRotation | null = null
): TScreenRotation {
  if (mode !== 'native') {
    return logical
  }
  if (nativeApplied == null) {
    return '0'
  }
  return cssRotationDelta(logical, nativeApplied)
}

export function cssEffectiveScreenRotationFromState(
  state: ICssEffectiveRotationState
): TScreenRotation {
  return cssEffectiveScreenRotation(
    state.viewportRotationMode,
    state.screenRotation,
    state.nativeAppliedRotation
  )
}

/**
 * Angle for live aspect math (swap at 90/270). CSS classes stay on {@link cssEffectiveScreenRotation}.
 *
 * A landscape framebuffer plus a native remainder of 0 is classified horizontal, so a 16:9 video
 * plays in the vertical orientation. When the window does not match the logical angle, return that
 * angle so the aspect filter swaps. When the window is already that shape (letterboxed strip or a
 * phone that really rotated), return 0 so we do not swap a second time.
 */
export function cssEffectiveRotationForWindow(
  mode: TViewportRotationMode,
  logical: TScreenRotation,
  nativeApplied: TScreenRotation | null,
  width: number,
  height: number
): TScreenRotation {
  if (viewportAspectMatchesLogicalRotation(logical, width, height)) {
    return '0'
  }
  if (
    mode === 'native'
    && nativeApplied != null
    && !viewportAspectMatchesLogicalRotation(nativeApplied, width, height)
  ) {
    return logical
  }
  return cssEffectiveScreenRotation(mode, logical, nativeApplied)
}

/** Orientation class for {@link cssEffectiveScreenRotation} (0/180 horizontal, 90/270 vertical). */
export function cssEffectiveScreenOrientation(
  mode: TViewportRotationMode,
  logical: TScreenRotation,
  nativeApplied: TScreenRotation | null = null
): TScreenOrientation {
  const rotation = cssEffectiveScreenRotation(mode, logical, nativeApplied)
  return rotation === '90' || rotation === '270' ? 'vertical' : 'horizontal'
}

export function cssEffectiveScreenOrientationFromState(
  state: ICssEffectiveRotationState
): TScreenOrientation {
  const rotation = cssEffectiveScreenRotationFromState(state)
  return rotation === '90' || rotation === '270' ? 'vertical' : 'horizontal'
}
